import { useState, useEffect, useRef, useMemo } from 'react';
import JsBarcode from 'jsbarcode';
import api from '@/services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Printer } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

/**
 * PricingSticker modal
 *
 * Renders a print-ready sheet of pricing stickers (50mm x 25mm, 2 per row).
 * Each sticker shows: Product Name, Selling Price, Barcode (CODE128).
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - productId: string (required when open)
 *  - defaultSellingPrice: number (fallback if API doesn't return it)
 */
const PricingSticker = ({ open, onClose, productId, defaultSellingPrice }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [selectedBarcode, setSelectedBarcode] = useState('');
  const [qty, setQty] = useState(1);
  const previewRef = useRef(null);

  // Fetch product when modal opens
  useEffect(() => {
    if (!open || !productId) return;
    (async () => {
      setLoading(true);
      try {
        const resp = await api.get(`/products/${productId}`);
        const prod = resp.data;
        setProduct(prod);
        // Prefer default barcode -> primary SKU barcode -> first in list
        const list = (prod?.barcodes || []).map((b) => b?.barcode || b).filter(Boolean);
        const defaultBc = (prod?.barcodes || []).find((b) => b?.is_default)?.barcode;
        setSelectedBarcode(defaultBc || list[0] || prod?.barcode || prod?.sku || '');
        setQty(1);
      } catch (e) {
        toast({ title: 'Error', description: 'Could not load product', variant: 'destructive' });
        onClose?.();
      } finally {
        setLoading(false);
      }
    })();
  }, [open, productId]);

  const barcodeOptions = useMemo(() => {
    if (!product) return [];
    const list = (product.barcodes || []).map((b) => ({
      value: b?.barcode || b,
      label: `${b?.barcode || b}${b?.is_default ? ' (default)' : ''}${b?.description ? ' - ' + b.description : ''}`,
    })).filter((o) => o.value);
    // Fallback: product.barcode (single flat field) when the barcodes array is empty
    if (list.length === 0 && product.barcode) {
      list.push({ value: product.barcode, label: product.barcode });
    }
    return list;
  }, [product]);

  const printName = useMemo(() => {
    const n = (product?.name || '').trim();
    return n.length > 35 ? n.slice(0, 35).trim() : n;
  }, [product]);

  const sellingPrice = useMemo(() => {
    const raw = !product
      ? (defaultSellingPrice || 0)
      : (product.selling_price ?? product.price ?? product.mrp ?? defaultSellingPrice ?? 0);
    // Shelf label shows a whole-rupee price (1248.75 → 1249.00)
    return Math.round(Number(raw) || 0);
  }, [product, defaultSellingPrice]);

  // Draw barcodes into every <svg> inside the preview once the modal content renders
  useEffect(() => {
    if (!open || !previewRef.current || !selectedBarcode) return;
    const svgs = previewRef.current.querySelectorAll('svg.sticker-barcode');
    svgs.forEach((svg) => {
      try {
        JsBarcode(svg, String(selectedBarcode), {
          format: 'CODE128',
          displayValue: true,
          fontSize: 12,
          font: 'Helvetica, Arial, sans-serif',
          textMargin: 0,
          margin: 0,
          height: 36,
          width: 1.6,
          background: '#ffffff',
          lineColor: '#000000',
        });
      } catch (e) {
        // ignore render error
      }
    });
  }, [open, selectedBarcode, qty, product]);

  const handlePrint = () => {
    if (!previewRef.current) return;
    const total = items.length;
    if (total === 0) return;

    // Build rows of 2 stickers each (matches Zebra 104mm x 25mm media:
    // two 50mm stickers + 4mm center gutter = 104mm per feed advance).
    // We serialise each sticker's inner HTML (which already contains the
    // live-rendered JsBarcode SVG) so the print window needs zero JS.
    const stickers = Array.from(previewRef.current.querySelectorAll('.sticker'));
    const rows = [];
    for (let i = 0; i < stickers.length; i += 2) {
      const left = stickers[i].outerHTML;
      const right = stickers[i + 1] ? stickers[i + 1].outerHTML
                                    : '<div class="sticker sticker-blank"></div>';
      rows.push(`<div class="label-row">${left}${right}</div>`);
    }

    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) {
      toast({ title: 'Pop-up blocked', description: 'Please allow pop-ups to print labels.', variant: 'destructive' });
      return;
    }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"/>
      <title>Price Labels - ${product?.name || ''}</title>
      <style>${stickerCss(true)}</style>
    </head><body>${rows.join('')}
    <script>window.addEventListener('load',function(){setTimeout(function(){window.print();},300);});</script>
    </body></html>`);
    w.document.close();
  };

  const items = Array.from({ length: Math.max(0, Math.min(500, Number(qty) || 0)) });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pricing-sticker-dialog">
        <DialogHeader>
          <DialogTitle>Print Price Labels</DialogTitle>
        </DialogHeader>

        {loading || !product ? (
          <div className="flex items-center justify-center py-10 text-gray-500">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading product...
          </div>
        ) : (
          <>
            {/* Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>Product</Label>
                <div className="px-3 py-2 rounded border bg-gray-50 text-sm font-medium" data-testid="sticker-product-name">
                  {product.name}
                </div>
              </div>
              <div className="space-y-1">
                <Label>Barcode</Label>
                <Select value={selectedBarcode} onValueChange={setSelectedBarcode}>
                  <SelectTrigger data-testid="sticker-barcode-select"><SelectValue placeholder="Select barcode" /></SelectTrigger>
                  <SelectContent>
                    {barcodeOptions.length === 0 ? (
                      <SelectItem value="__none__" disabled>No barcode on product</SelectItem>
                    ) : barcodeOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Qty to Print</Label>
                <Input
                  type="number" min={1} max={500} value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
                  data-testid="sticker-qty-input"
                />
              </div>
            </div>

            {/* Preview */}
            <div className="mt-4">
              <div className="text-sm text-gray-500 mb-2">
                Preview — {items.length} sticker{items.length === 1 ? '' : 's'} · media 104&nbsp;mm × 25&nbsp;mm (2 × 50&nbsp;mm + 4&nbsp;mm gutter)
              </div>
              <style>{stickerCss(false)}</style>
              <div className="border rounded bg-gray-100 p-3 overflow-y-auto flex flex-col items-center" style={{ maxHeight: '48vh' }}>
                <div ref={previewRef}>
                  {Array.from({ length: Math.ceil(items.length / 2) }).map((_, rowIdx) => {
                    const first = rowIdx * 2;
                    const second = first + 1;
                    return (
                      <div className="label-row" key={rowIdx}>
                        <div className="sticker">
                          <div className="sticker-name">{printName}</div>
                          <div className="sticker-price">₹ {Number(sellingPrice).toFixed(2)}</div>
                          <div className="sticker-barcode-wrap">
                            <svg className="sticker-barcode"></svg>
                          </div>
                        </div>
                        {second < items.length ? (
                          <div className="sticker">
                            <div className="sticker-name">{printName}</div>
                            <div className="sticker-price">₹ {Number(sellingPrice).toFixed(2)}</div>
                            <div className="sticker-barcode-wrap">
                              <svg className="sticker-barcode"></svg>
                            </div>
                          </div>
                        ) : (
                          <div className="sticker sticker-blank"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={onClose} data-testid="sticker-cancel-btn">Cancel</Button>
              <Button
                onClick={handlePrint}
                disabled={!selectedBarcode || items.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="sticker-print-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print {items.length} Label{items.length === 1 ? '' : 's'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Shared sticker CSS. In print mode the @page is set to exactly match the
 * physical media (104mm wide x 25mm tall - two 50mm stickers side by side
 * with a 4mm center gutter) so the Zebra driver prints 1:1 with no rotation
 * and no scaling. On-screen preview mirrors the same geometry so WYSIWYG.
 */
function stickerCss(isPrint) {
  return `
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: ${isPrint ? '#fff' : '#f3f4f6'}; }

    .label-row {
      width: 104mm;
      height: 25mm;
      display: flex;
      flex-direction: row;
      align-items: stretch;
      margin: 0 auto ${isPrint ? '0' : '2mm'};
      background: #fff;
      page-break-after: always;
      page-break-inside: avoid;
      break-after: page;
      break-inside: avoid;
    }
    .label-row:last-child { page-break-after: auto; break-after: auto; }

    .sticker {
      width: 50mm;
      height: 25mm;
      background: #ffffff;
      padding: 2mm 1.5mm 1mm;
      display: grid;
      grid-template-rows: 7mm 6mm 10mm;
      row-gap: 0;
      overflow: hidden;
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      color: #000;
      border: ${isPrint ? '0' : '1px dashed #9ca3af'};
    }
    /* 4mm center gutter between the two stickers = 50 + 4 + 50 = 104mm */
    .label-row > .sticker:first-child { margin-right: 4mm; }
    .sticker-blank { background: transparent; border: 0; }

    .sticker-name {
      font-size: 8pt;
      font-weight: 800;
      line-height: 1.05;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-align: center;
    }
    .sticker-price {
      font-size: 12pt;
      font-weight: 900;
      text-align: center;
      line-height: 1;
    }
    .sticker-barcode-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    .sticker-barcode { width: 46mm; height: 10mm; }

    @media print {
      @page { size: 104mm 25mm; margin: 0; }
      html, body { background: #fff !important; }
      .label-row { margin: 0 auto; }
      .sticker { border: 0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;
}

export default PricingSticker;
